// A Shipments-panel row's title and one-line preview — a Shipment, Order,
// Pack, Tare or Item HL loop's HL03 decides which preview fields apply.
// Split out of ShipmentTree.tsx so the recursive rendering component stays
// focused on rendering, not on reading field values.

import type {SectionGroup} from './sections';
import type {EdiElementValue, EdiSegment} from './tokenizer';

function text(value: EdiElementValue | undefined): string {
  return typeof value === 'string' ? value : '';
}

function find(group: SectionGroup, id: string): EdiSegment | undefined {
  return group.entries.find(e => e.segment.id === id)?.segment;
}

const LEVEL_NAMES: Record<string, string> = {
  S: 'Shipment',
  O: 'Order',
  P: 'Pack',
  T: 'Tare',
  I: 'Item',
};

function shipmentPreview(group: SectionGroup): string {
  const td5 = find(group, 'TD5');
  const carrier = text(td5?.elements[2]);
  const routing = text(td5?.elements[4]);
  if (carrier || routing) {
    return [carrier, routing].filter(Boolean).join(' · ');
  }
  const dtm = find(group, 'DTM');
  const shipDate = text(dtm?.elements[1]);
  return shipDate ? `Ship date ${shipDate}` : '';
}

function orderPreview(group: SectionGroup): string {
  const prf = find(group, 'PRF');
  const po = text(prf?.elements[0]);
  return po ? `PO ${po}` : '';
}

function packOrTarePreview(group: SectionGroup): string {
  const man = find(group, 'MAN');
  const marks = text(man?.elements[1]);
  return marks ? `Marks ${marks}` : '';
}

function itemPreview(group: SectionGroup): string {
  const lin = find(group, 'LIN');
  const sn1 = find(group, 'SN1');
  const productId = text(lin?.elements[2]);
  const qty = text(sn1?.elements[1]);
  const uom = text(sn1?.elements[2]);
  const qtyText = qty ? `Qty ${qty}${uom ? ` ${uom}` : ''}` : '';
  return [productId, qtyText].filter(Boolean).join(' · ');
}

/** The title and one-line preview for one Shipments-panel row, derived from
 * its HL segment (id and level code) and whichever of TD5/DTM (Shipment),
 * PRF (Order), MAN (Pack/Tare) or LIN/SN1 (Item) its level code says to
 * read from. */
export function hlRowTitleAndPreview(group: SectionGroup): {
  title: string;
  preview: string;
} {
  const hl = find(group, 'HL');
  const id = text(hl?.elements[0]);
  const levelCode = text(hl?.elements[2]);
  const levelName = LEVEL_NAMES[levelCode] ?? 'Level';
  const title = id ? `${levelName} ${id}` : levelName;
  const preview =
    levelCode === 'S'
      ? shipmentPreview(group)
      : levelCode === 'O'
        ? orderPreview(group)
        : levelCode === 'P' || levelCode === 'T'
          ? packOrTarePreview(group)
          : levelCode === 'I'
            ? itemPreview(group)
            : '';
  return {title, preview};
}
